// import statements stay the same
import React, { useState } from "react";
import {
  Drawer,
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from "@mui/material";
import {
  getLocalValue,
  IsSmallScreen,
  LoacalVariables
} from "../../commonFunctions/CommonFunctions";
import { getMenu } from "./menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Link, useLocation } from "react-router-dom";
import logo from "../../images/logo.svg";

interface MenuItem {
  id: number;
  label: string;
  path?: string;
  icon: React.ElementType;
  children?: MenuItem[];
}

interface SidebarProps {
  open: boolean;
  handleDrawerToggle: () => void;
  handleCollapse: () => void;
  collapse: boolean;
  drawerWidth?: number;

  primaryColor: string;
  secondaryColor: string;
  hoverColor: string;
  textColor: string;
  iconColor: string;
  hoverTextColor: string;
  backgroundColor: string;
  selectedBackgroundColor: string;
  boxShadowColor: string;
}

const Sidebar2: React.FC<SidebarProps> = ({
  open,
  handleDrawerToggle,
  collapse,
  drawerWidth = 240,

  primaryColor,
  secondaryColor,
  textColor,
  hoverColor,
  hoverTextColor,
  backgroundColor,
  selectedBackgroundColor,
  boxShadowColor,
  iconColor
}) => {
  const isMobile = IsSmallScreen();
  const location = useLocation();
  const userType: any = getLocalValue(LoacalVariables.UserType);
  const menu = getMenu(userType);

  const [openChildMenus, setOpenChildMenus] = useState<Record<number, boolean>>({});
  const [hoveredMenuId, setHoveredMenuId] = useState<number | null>(null);
  const [hoveredMenuPosition, setHoveredMenuPosition] = useState({ top: 0 });

  const isSelected = (path?: string) => path && location.pathname === path;

  const handleToggleChildMenu = (id: number) => {
    setOpenChildMenus((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleMouseEnter = (id: number) => {
    setHoveredMenuId(id);
    const listItem = document.getElementById(`menu-item-${id}`);
    if (listItem) {
      const rect = listItem.getBoundingClientRect();
      setHoveredMenuPosition({ top: rect.top + window.scrollY });
    }
  };

  const handleMouseLeave = () => {
    setHoveredMenuId(null);
  };

  return (
    <Drawer
      variant={"persistent"}
      anchor="left"
      open={open}
      onClose={handleDrawerToggle}
      sx={{
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: collapse ? 80 : drawerWidth,
          transition: "width 0.3s ease",
          overflowX: "hidden",
          boxSizing: "border-box"
        }
      }}
    >
      <Box sx={{ padding: collapse ? ".2rem" : "1.1rem" }}>
        <Typography sx={{ fontWeight: "bold", fontSize: "1.2rem", textAlign: "center" }}>
          <img src={logo} alt="logo" width={collapse ? 60 : 80} height={collapse ? 50 : 20} />
        </Typography>
      </Box>

      <Divider />

      <List>
        {menu.staticMenu.map((item: MenuItem) => (
          <React.Fragment key={item.id}>
            <li
              id={`menu-item-${item.id}`}
              style={{ padding: 4 }}
              onMouseEnter={() => handleMouseEnter(item.id)}
              onMouseLeave={handleMouseLeave}
            >
              <ListItemButton
                component={Link}
                to={item.path || "#"}
                onClick={() => item.children && handleToggleChildMenu(item.id)}
                sx={{
                  minHeight: 48,
                  justifyContent: collapse ? "center" : "initial",
                  px: collapse ? 1.5 : 2.5,
                  borderRadius: "7px",
                  backgroundColor: isSelected(item.path) ? hoverColor : "transparent",
                  color: isSelected(item.path) ? hoverTextColor : textColor,
                  transition: "background-color 0.3s ease",
                  "&:hover": {
                    backgroundColor: hoverColor,
                    color: hoverTextColor
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    width: 24,
                    height: 24,
                    mr: collapse ? 0 : 2,
                    color: isSelected(item.path) ? hoverTextColor : iconColor,
                    justifyContent: "center"
                  }}
                >
                  <item.icon />
                </ListItemIcon>
                {!collapse && (
                  <ListItemText
                    primary={item.label}
                    sx={{ "& .MuiTypography-root": { fontSize: "14px" } }}
                  />
                )}
                {item.children && !collapse && (
                  openChildMenus[item.id] ? (
                    <ExpandMoreIcon sx={{ color: textColor }} />
                  ) : (
                    <ExpandLessIcon sx={{ color: textColor }} />
                  )
                )}
              </ListItemButton>
            </li>

            {/* Hover Menu (collapsed state) */}
            {item.children && collapse && hoveredMenuId === item.id && (
              <ul
                className="child-menu"
                style={{
                  position: "fixed",
                  left: 80,
                  top: hoveredMenuPosition.top,
                  zIndex: 1500,
                  backgroundColor: "#fff",
                  listStyle: "none",
                  borderRadius: 10,
                  boxShadow: `0 10px 30px ${boxShadowColor}`,
                  margin: 0,
                  padding: "0.5rem 0"
                }}
                onMouseEnter={() => setHoveredMenuId(item.id)}
                onMouseLeave={handleMouseLeave}
              >
                {item.children.map((child) => (
                  <li key={child.id} style={{ width: "14rem" }}>
                    <ListItemButton
                      component={Link}
                      to={child.path || "#"}
                      sx={{
                        height: 45,
                        color: textColor,
                        borderRadius: 2,
                        "&:hover": {
                          color: hoverTextColor,
                          backgroundColor: hoverColor
                        }
                      }}
                    >
                      <ListItemText
                        primary={child.label}
                        sx={{ "& .MuiTypography-root": { fontSize: "14px" } }}
                      />
                    </ListItemButton>
                  </li>
                ))}
              </ul>
            )}

            {/* Expandable Child Menu (animated) */}
            {!collapse && item.children && (
              <Box
                sx={{
                  maxHeight: openChildMenus[item.id] ? "500px" : 0,
                  opacity: openChildMenus[item.id] ? 1 : 0,
                  overflow: "hidden",
                  transition: "max-height 0.4s ease, opacity 0.3s ease",
                  ml: 3
                }}
              >
                {item.children.map((child) => (
                  <ListItemButton
                    key={child.id}
                    component={Link}
                    to={child.path || "#"}
                    sx={{
                      backgroundColor: isSelected(child.path) ? hoverColor : "transparent",
                      color: isSelected(child.path) ? hoverTextColor : textColor,
                      my: 0.5,
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: hoverColor,
                        color: hoverTextColor
                      }
                    }}
                  >
                    <ListItemText
                      primary={child.label}
                      sx={{ "& .MuiTypography-root": { fontSize: "14px" } }}
                    />
                  </ListItemButton>
                ))}
              </Box>
            )}
          </React.Fragment>
        ))}
      </List>
    </Drawer>
  );
};

export default Sidebar2;
